import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFirestoreInstance,
  getAuthInstance,
  waitForAuthBridge,
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import type { Firestore } from 'firebase/firestore';
import type { ChatMessage, GifData, TrackData, LeagueData } from './use-league-chat';
import { filterProfanity } from '@/lib/profanity-filter';
import { toast } from '@/components/ui/use-toast';
import { apiUrl } from '@/config/api';

const FIRST_MSG_KEY = 'yapzap-community-first-msg-sent';

// Rate limiting: max 8 messages per 30 seconds
const RATE_LIMIT_MAX = 8;
const RATE_LIMIT_WINDOW_MS = 30_000;

// Re-export types for convenience
export type { ChatMessage, GifData, TrackData, LeagueData };

interface UsePublicChatOptions {
  channelId: number;
  userId: string | null;
  username: string | null;
  avatarUrl: string | null;
  enabled?: boolean;
  messageLimit?: number;
}

interface SendMessageOptions {
  content: string;
  contentType?: 'text' | 'image' | 'gif' | 'track' | 'league';
  imageUrl?: string;
  gifData?: GifData;
  trackData?: TrackData;
  leagueData?: LeagueData;
}

interface UsePublicChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (options: SendMessageOptions) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  isSending: boolean;
  justSentFirstMessage: boolean;
  clearFirstMessageFlag: () => void;
}

/**
 * Hook to manage real-time chat messages for public community channels using Firestore.
 * Adapted from useDailyChat with publicChats collection.
 */
export function usePublicChat({
  channelId,
  userId,
  username,
  avatarUrl,
  enabled = true,
  messageLimit: msgLimit = 100,
}: UsePublicChatOptions): UsePublicChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [justSentFirstMessage, setJustSentFirstMessage] = useState(false);
  const firestoreRef = useRef<Firestore | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const messageTimestampsRef = useRef<number[]>([]);

  const clearFirstMessageFlag = useCallback(() => {
    setJustSentFirstMessage(false);
  }, []);

  // Initialize Firestore and set up listener
  useEffect(() => {
    if (!enabled || !channelId) {
      setIsLoading(false);
      return;
    }

    // Reset loading state when (re-)enabling so consumers know we're connecting
    setIsLoading(true);

    let isMounted = true;
    let loadingTimeoutId: NodeJS.Timeout | null = null;

    // Global timeout
    loadingTimeoutId = setTimeout(() => {
      if (isMounted) {
        console.error('Public Chat: Global loading timeout reached (30s)');
        setError('Chat took too long to load. Please try again.');
        setIsLoading(false);
      }
    }, 30000);

    const setupListener = async () => {
      try {
        // On native platforms, wait for the auth bridge to complete first
        if (Capacitor.isNativePlatform()) {
          try {
            await Promise.race([
              waitForAuthBridge(),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Auth bridge timed out')), 15000)
              ),
            ]);
          } catch (bridgeError: any) {
            console.warn('Public Chat: Auth bridge failed/timed out:', bridgeError?.message);
          }
        }

        const [firestore, auth] = await Promise.race([
          Promise.all([
            getFirestoreInstance(),
            getAuthInstance()
          ]),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Firebase initialization timed out')), 10000)
          ),
        ]) as [Firestore | null, any];

        if (!firestore) {
          throw new Error('Firestore not initialized');
        }
        firestoreRef.current = firestore;

        // Wait for auth state to be determined
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => resolve(), 5000);
          const unsubAuth = onAuthStateChanged(auth, () => {
            clearTimeout(timeout);
            unsubAuth();
            resolve();
          }, () => {
            clearTimeout(timeout);
            resolve();
          });
        });

        // Use publicChats collection
        const messagesRef = collection(firestore, `publicChats/${channelId}/messages`);
        const messagesQuery = query(
          messagesRef,
          orderBy('createdAt', 'desc'),
          limit(msgLimit)
        );

        // Set up real-time listener
        unsubscribeRef.current = onSnapshot(
          messagesQuery,
          (snapshot) => {
            if (!isMounted) return;

            const newMessages: ChatMessage[] = [];
            snapshot.forEach((doc) => {
              const data = doc.data();
              newMessages.push({
                id: doc.id,
                userId: data.userId,
                username: data.username,
                avatarUrl: data.avatarUrl,
                content: data.content,
                contentType: data.contentType || 'text',
                imageUrl: data.imageUrl || null,
                gifData: data.gifData || null,
                trackData: data.trackData || null,
                leagueData: data.leagueData || null,
                isDeleted: data.isDeleted || false,
                deletedBy: data.deletedBy || null,
                deletedAt: data.deletedAt?.toDate() || null,
                createdAt: data.createdAt?.toDate() || new Date(),
                replyToId: data.replyToId || null,
              });
            });

            // Reverse to show oldest first
            setMessages(newMessages.reverse());
            setIsLoading(false);
            setError(null);
            if (loadingTimeoutId) {
              clearTimeout(loadingTimeoutId);
              loadingTimeoutId = null;
            }
          },
          (err) => {
            if (!isMounted) return;
            console.error('Public chat listener error:', err);
            if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
              setError('Chat authentication failed. Please sign out and sign back in to access chat.');
            } else {
              setError('Failed to load chat messages');
            }
            setIsLoading(false);
            if (loadingTimeoutId) {
              clearTimeout(loadingTimeoutId);
              loadingTimeoutId = null;
            }
          }
        );
      } catch (err: any) {
        if (!isMounted) return;
        console.error('Public chat setup error:', err);
        if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
          setError('Chat authentication failed. Please sign out and sign back in to access chat.');
        } else {
          setError('Failed to connect to chat');
        }
        setIsLoading(false);
        if (loadingTimeoutId) {
          clearTimeout(loadingTimeoutId);
          loadingTimeoutId = null;
        }
      }
    };

    setupListener();

    return () => {
      isMounted = false;
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
        loadingTimeoutId = null;
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [channelId, enabled, msgLimit]);

  // Send a message
  const sendMessage = useCallback(
    async (options: SendMessageOptions) => {
      const { content, contentType = 'text', imageUrl, gifData, trackData, leagueData } = options;

      const needsContent = contentType === 'text';
      const hasRequiredContent = needsContent ? content.trim() : true;

      if (!userId || !username || !firestoreRef.current || !hasRequiredContent) {
        return;
      }

      // Rate limiting check
      const now = Date.now();
      const recentTimestamps = messageTimestampsRef.current.filter(
        (t) => now - t < RATE_LIMIT_WINDOW_MS
      );
      if (recentTimestamps.length >= RATE_LIMIT_MAX) {
        setError("Slow down! You're sending messages too fast.");
        setTimeout(() => setError(null), 4000);
        return;
      }

      setIsSending(true);
      setError(null);

      try {
        const messagesRef = collection(firestoreRef.current, `publicChats/${channelId}/messages`);

        // Apply profanity filter to text content
        const trimmedContent = content.trim().slice(0, 500);
        const filteredContent = filterProfanity(trimmedContent);

        if (filteredContent !== trimmedContent) {
          toast({ description: 'Some words were filtered.', duration: 3000 });
        }

        // Track timestamp for rate limiting
        messageTimestampsRef.current = [...recentTimestamps, now];

        await addDoc(messagesRef, {
          userId,
          username,
          avatarUrl,
          content: filteredContent,
          contentType,
          imageUrl: imageUrl || null,
          gifData: gifData || null,
          trackData: trackData || null,
          leagueData: leagueData || null,
          isDeleted: false,
          deletedBy: null,
          deletedAt: null,
          createdAt: serverTimestamp(),
          replyToId: null,
        });

        // Fire-and-forget: notify channel subscribers
        fetch(apiUrl(`/api/community/channels/${channelId}/notify`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ senderUsername: username, messagePreview: filteredContent.slice(0, 100) }),
        }).catch(() => {});

        // First-message detection for tour trigger
        if (!localStorage.getItem(FIRST_MSG_KEY)) {
          localStorage.setItem(FIRST_MSG_KEY, '1');
          setJustSentFirstMessage(true);
        }
      } catch (err: any) {
        console.error('Send public message error:', err?.code, err?.message, { channelId, userId });

        if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
          setError('Unable to send message. Try signing out and back in.');
        } else {
          setError('Failed to send message');
        }
      } finally {
        setIsSending(false);
      }
    },
    [channelId, userId, username, avatarUrl]
  );

  // Delete a message (soft-delete)
  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!userId || !firestoreRef.current) return;

      try {
        const messageRef = doc(firestoreRef.current, `publicChats/${channelId}/messages`, messageId);
        await updateDoc(messageRef, {
          isDeleted: true,
          content: '',
          deletedBy: userId,
          deletedAt: serverTimestamp(),
        });
      } catch (err: any) {
        console.error('Delete public message error:', err?.code, err?.message);
        setError('Failed to delete message');
      }
    },
    [channelId, userId]
  );

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    deleteMessage,
    isSending,
    justSentFirstMessage,
    clearFirstMessageFlag,
  };
}
